import { useEffect, useMemo, useState } from 'react'
import { EventNames } from '../../../../types/misc.types'
import { trackEvent } from '../../../../utils/appBridge'
import BottomSheetContent from '../../BottomSheetContent'
import Tabs from '../../../ui-components/Tabs'
import { MonthlyCalendar } from './MonthlyCalendar'
import { WeeklyCalendar } from './WeeklyCalendar'
import { DailyCalendar } from './DailyCalendar'
import { FrequencyType, WeekDay, CalendarSelection, Day } from '../../../../types/calendar.types'
import { getNextSIPDate, getWhyNotSIPDate } from '../../../../utils/mf-execution/calendar'
import { convertEpochToDate, toPascalCaseWithSpaces } from '../../../../utils/utils'
import { LocalToast } from '../../../ui-components/LocalToast'
import { useToast } from '../../../../state-management/contexts/ToastContext'
import { PrimaryButton } from '../../../execution/PrimaryButton'
import { getBottomPadding } from '../../../../utils/sessionStorage'

type CalendarBottomSheetProps = {
  onDismiss: () => void
  ScreenName: string
  availableMonthlyDays: Day[]
  availableWeeklyDays: Day[]
  availableDailyDays: Day[]
  selectedFrequency?: FrequencyType
  selectedValue: number
  onSelectionChange?: (selection: CalendarSelection) => void
  onWhyNotSIPClick?: (nextSIPDate: number, whyNotSIPDate: number) => void
  trackingFund?: { fundIsin: string; fundName: string }
}

// Constants moved outside component to prevent recreation
const WEEK_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

export const CalendarBottomSheet = ({
  onDismiss,
  ScreenName,
  availableMonthlyDays = [],
  availableWeeklyDays = [],
  availableDailyDays = [],
  selectedFrequency = FrequencyType.MONTHLY,
  selectedValue,
  onSelectionChange,
  onWhyNotSIPClick,
  trackingFund
}: CalendarBottomSheetProps) => {
  const { showToast } = useToast()

  const [selection, setSelection] = useState<CalendarSelection>({
    frequencyType: selectedFrequency,
    value: selectedValue
  })

  const [monthlySelection, setMonthlySelection] = useState<CalendarSelection>({
    frequencyType: FrequencyType.MONTHLY,
    value: selectedFrequency === FrequencyType.MONTHLY ? selectedValue : (availableMonthlyDays?.[0]?.day ?? 1)
  })

  const [weeklySelection, setWeeklySelection] = useState<CalendarSelection>({
    frequencyType: FrequencyType.WEEKLY,
    value:
      selectedFrequency === FrequencyType.WEEKLY ? selectedValue : (availableWeeklyDays?.[0]?.day ?? WeekDay.MONDAY)
  })

  const [dailySelection, setDailySelection] = useState<CalendarSelection>({
    frequencyType: FrequencyType.DAILY,
    value: selectedFrequency === FrequencyType.DAILY ? selectedValue : (availableDailyDays?.[0]?.day ?? 1)
  })

  const availableFrequencies = useMemo(() => {
    const frequencies: FrequencyType[] = []
    if (availableMonthlyDays?.length > 0) frequencies.push(FrequencyType.MONTHLY)
    if (availableWeeklyDays?.length > 0) frequencies.push(FrequencyType.WEEKLY)
    if (availableDailyDays?.length > 0) frequencies.push(FrequencyType.DAILY)
    return frequencies
  }, [availableMonthlyDays, availableWeeklyDays, availableDailyDays])

  const frequencyTabs = useMemo(
    () =>
      availableFrequencies.map((freq) => ({
        id: freq,
        label: toPascalCaseWithSpaces(freq),
        testLocator: `MFE_SELECT_SIP_FREQ_${freq?.toUpperCase()}`
      })),
    [availableFrequencies]
  )

  // Memoize expensive calculations
  const nextSIPDate = useMemo(
    () => getNextSIPDate(selection, availableMonthlyDays, availableWeeklyDays, availableDailyDays),
    [selection, availableMonthlyDays, availableWeeklyDays, availableDailyDays]
  )

  const whyNotSIPDate = useMemo(
    () => getWhyNotSIPDate(selection, availableMonthlyDays, availableWeeklyDays, availableDailyDays),
    [selection, availableMonthlyDays, availableWeeklyDays, availableDailyDays]
  )

  const getDaySuffix = (day: number) => {
    if (day >= 11 && day <= 13) return 'th'
    switch (day % 10) {
      case 1:
        return 'st'
      case 2:
        return 'nd'
      case 3:
        return 'rd'
      default:
        return 'th'
    }
  }

  // Memoize frequency text to avoid recalculation on every render
  const frequencyText = useMemo(() => {
    switch (selection?.frequencyType) {
      case FrequencyType.WEEKLY:
        const weekDayName = selection?.value ? WEEK_DAYS[selection?.value - 1] : ''
        return `Every ${weekDayName}`
      case FrequencyType.DAILY:
        return `Every ${selection?.value} day${selection?.value && selection?.value > 1 ? 's' : ''}`
      case FrequencyType.MONTHLY:
      default:
        const daySuffix = getDaySuffix(selection?.value ?? 0)
        return `${selection?.value}${daySuffix} of every month`
    }
  }, [selection?.frequencyType, selection?.value])

  const handleSelectionChange = (newSelection: CalendarSelection) => {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName,
        eventId: 'MFE_SELECT_SIP_FREQ_SELECT',
        buttonLabel:
          newSelection?.frequencyType === FrequencyType.WEEKLY
            ? WEEK_DAYS[(newSelection?.value ?? 1) - 1]
            : newSelection?.frequencyType === FrequencyType.DAILY
              ? `Every ${newSelection?.value} day(s)`
              : `${newSelection?.value} of month`,
        ...(trackingFund ?? {})
      }
    })
    setSelection(newSelection)
    onSelectionChange?.(newSelection)
    if (newSelection?.frequencyType === FrequencyType.MONTHLY) {
      setMonthlySelection(newSelection)
    } else if (newSelection?.frequencyType === FrequencyType.WEEKLY) {
      setWeeklySelection(newSelection)
    } else if (newSelection?.frequencyType === FrequencyType.DAILY) {
      setDailySelection(newSelection)
    }
  }

  const getSelectionValueForFrequency = (frequency: FrequencyType): number => {
    switch (frequency) {
      case FrequencyType.MONTHLY:
        return monthlySelection?.value
      case FrequencyType.WEEKLY:
        return weeklySelection?.value
      case FrequencyType.DAILY:
        return dailySelection?.value
    }
  }

  const handleFrequencyChange = (frequencyId: string | number) => {
    const frequency = frequencyId as FrequencyType
    const newSelection: CalendarSelection = {
      frequencyType: frequency,
      value: getSelectionValueForFrequency(frequency)
    }
    handleSelectionChange(newSelection)
  }

  const handleDisabledDayClick = () => {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName,
        eventId: 'MFE_SELECT_SIP_FREQ_DISABLED_DAY',
        buttonLabel: 'Disabled day',
        ...(trackingFund ?? {})
      }
    })
    showToast({ message: 'SIP date unavailable', toastToBeShownById: 'CalendarBottomSheet' })
  }

  const renderCalendarUI = useMemo(() => {
    const commonProps = {
      selection,
      onSelectionChange: handleSelectionChange
    }

    switch (selection?.frequencyType) {
      case FrequencyType.MONTHLY:
        return (
          <MonthlyCalendar
            {...commonProps}
            availableMonthlyDays={availableMonthlyDays}
            handleDisabledDayClick={handleDisabledDayClick}
          />
        )
      case FrequencyType.WEEKLY:
        return <WeeklyCalendar {...commonProps} availableWeeklyDays={availableWeeklyDays} />
      case FrequencyType.DAILY:
        return <DailyCalendar {...commonProps} availableDailyDays={availableDailyDays} />
      default:
        return (
          <MonthlyCalendar
            {...commonProps}
            availableMonthlyDays={availableMonthlyDays}
            handleDisabledDayClick={handleDisabledDayClick}
          />
        )
    }
  }, [selection, availableMonthlyDays, availableWeeklyDays, availableDailyDays])

  const whyNotSection = useMemo(() => {
    if (!whyNotSIPDate || !nextSIPDate) return null

    return (
      <>
        &nbsp;{' '}
        <span
          className='text-[#6A6E6E] font-[400] underline cursor-pointer'
          onClick={() => {
            trackEvent({
              eventName: EventNames.UserClicked,
              payload: {
                ScreenName,
                eventId: 'MFE_SELECT_SIP_WHY_NOT',
                buttonLabel: `Why not ${convertEpochToDate(whyNotSIPDate, '%d %b')}?`,
                ...(trackingFund ?? {})
              }
            })
            onWhyNotSIPClick?.(nextSIPDate, whyNotSIPDate)
          }}
        >
          {' '}
          Why not {convertEpochToDate(whyNotSIPDate, '%d %b')}?
        </span>
      </>
    )
  }, [whyNotSIPDate, nextSIPDate, ScreenName, trackingFund?.fundIsin, trackingFund?.fundName])

  useEffect(() => {
    trackEvent({
      eventName: EventNames.BottomSheetLoad,
      payload: {
        bottomSheetName: 'SIP frequency',
        ScreenName,
        ...(trackingFund ?? {})
      }
    })
  }, [ScreenName])

  return (
    <BottomSheetContent onDismiss={onDismiss} bottomSheetCtaClassName='!p-0' bottomSheetCtas={<></>}>
      <div test-locator='MFE_SELECT_SIP_FREQ' className='flex flex-col'>
        {/* SIP Date Header */}
        <div className=''>
          <div className='text-[#6A6E6E] text-[14px] font-[400] leading-[150%]'>SIP date</div>
          <div className='text-[#232323] text-[20px] font-[600] leading-[150%]'>{frequencyText}</div>
        </div>

        {/* Frequency Tabs */}
        {/* <div className='w-full pt-5'>
          <Tabs
            tabsData={frequencyTabs}
            selectedTabId={selection?.frequencyType}
            onTabClick={handleFrequencyChange}
            isFullWidth={true}
            selectedTabClassName='font-[500] !text-[14px]'
            tabClassName='font-[400] !text-[14px]'
            tabsClassName='!h-12 '
          />
        </div> */}

        {/* Calendar UI */}
        <div
          style={{ paddingTop: selection?.frequencyType === FrequencyType.MONTHLY ? '30px' : '20px' }}
          className='pb-6'
        >
          {renderCalendarUI}
        </div>

        {/* SIP Details */}
        <div className=' flex flex-col gap-2 pt-4 pb-6 border-t border-[#E5E7EB]'>
          <div className='text-[#6A6E6E] text-[13px] font-[400] leading-[150%]'>
            First SIP &nbsp;
            <span className='text-[#232323] font-[500]'> Today </span>
          </div>
          <div className='text-[#6A6E6E] text-[13px] font-[400] leading-[150%]'>
            Next SIP &nbsp;
            {nextSIPDate && (
              <span className='text-[#232323] font-[500]'> {convertEpochToDate(nextSIPDate, '%d %b')} </span>
            )}
            {whyNotSection}
          </div>
        </div>

        <div>
          <LocalToast width='200px' toastToBeShownById='CalendarBottomSheet' />
        </div>

        <div style={{ paddingBottom: `${getBottomPadding() + 8}px` }}>
          <PrimaryButton
            onClick={() => {
              trackEvent({
                eventName: EventNames.ButtonTap,
                payload: {
                  ScreenName,
                  eventId: 'MFE_SELECT_SIP_FREQ_CONFIRM',
                  buttonLabel: 'Confirm',
                  ...(trackingFund ?? {})
                }
              })
              onDismiss()
            }}
            label='Confirm'
            primaryButtonClassName='!h-[52px] !text-[16px] !font-[500]'
          />
        </div>
      </div>
    </BottomSheetContent>
  )
}
